/**
 * Common utility function to format AWS Account Vending preview details
 * Handles both live API responses and order details catalog config structures
 */

/**
 * Formats AWS Account Vending data for preview display
 * @param {Object} data - Can be either live API response or catalog config from order details
 * @param {string} source - 'api' for live preview API data, 'orderDetails' for validation report data
 * @returns {Object} Formatted preview data with generalInfo and resources
 */
export const formatAwsAccountVendingPreview = (data, source = 'api') => {
    if (!data) return null;

    // Determine data structure based on source
    const isApiResponse = source === 'api';

    // Helper function to check if a value exists and is not empty
    const hasValue = (value) => value !== null && value !== undefined && value !== '' && value !== 'N/A';

    // Extract general information - only include if values exist
    const generalInfo = {};

    if (hasValue(data?.applicationService)) {
        generalInfo['Application Service'] = data.applicationService;
    }
    if (hasValue(data?.businessApplication)) {
        generalInfo['Business Application'] = data.businessApplication;
    }
    if (hasValue(data?.catalogType)) {
        generalInfo['Catalog Type'] = data.catalogType;
    }

    const appOwner = isApiResponse
        ? data?.entityDetails?.applicationManagerGpid
        : data?.entityDetails?.applicationManagerGpid || data?.activeDirectoryGroupOwnerId;
    if (hasValue(appOwner)) {
        generalInfo['Application Owner'] = appOwner;
    }
    if (hasValue(data?.costCenter)) {
        generalInfo['Cost Center'] = data.costCenter;
    }
    if (hasValue(data?.dataClassification)) {
        generalInfo['Data Classification'] = data.dataClassification;
    }
    if (hasValue(data?.businessSponsor?.name)) {
        generalInfo['Business Sponsor'] = data.businessSponsor.name;
    }

    // Format AWS resource sections for display
    const resources = [];

    // 1. AWS Account Details - only include if values exist
    const accountProperties = {};

    if (hasValue(data?.awsAccountOnboardOrderId)) {
        accountProperties['AWS Cloud Onboarding Order ID'] = data.awsAccountOnboardOrderId;
    }
    if (hasValue(data?.applicationName)) {
        accountProperties['Application Name'] = data.applicationName;
    }
    if (hasValue(data?.applicationShortName)) {
        accountProperties['Application Short Name'] = data.applicationShortName;
    }
    if (hasValue(data?.awsAccountName)) {
        accountProperties['AWS Account Name'] = data.awsAccountName;
    }
    if (hasValue(data?.businessCategory)) {
        accountProperties['Business Category'] = data.businessCategory;
    }

    // Add AWS Email Address for API responses
    if (isApiResponse && hasValue(data?.techInputJson?.Email_Address)) {
        accountProperties['AWS Email Address'] = data.techInputJson.Email_Address;
    }

    // Only add AWS Account Details section if there are properties to display
    if (Object.keys(accountProperties).length > 0) {
        resources.push({
            'Resource Type': 'AWS Account Details',
            'Resource Properties': accountProperties,
        });
    }

    // 2. Landing Zone Details - only include if values exist
    const landingZoneProperties = {};

    if (hasValue(data?.sector)) {
        landingZoneProperties.Sector = data.sector;
    }
    if (hasValue(data?.department)) {
        landingZoneProperties.Department = data.department;
    }
    if (hasValue(data?.applicationRegion)) {
        landingZoneProperties['Application Region'] = data?.applicationRegion;
    }
    if (hasValue(data?.region)) {
        landingZoneProperties.Region = data?.region;
    }

    const environment = Array.isArray(data?.environment) ? data.environment.join(', ') : data?.environment;
    if (hasValue(environment)) {
        landingZoneProperties.Environment = environment;
    }

    if (hasValue(data?.ouName)) {
        landingZoneProperties['AWS Organizational Unit Name'] = data.ouName;
    }
    if (hasValue(data?.ouId)) {
        landingZoneProperties['AWS Organizational Unit ID'] = data.ouId;
    }
    if (hasValue(data?.sharedNetworkAccountName)) {
        landingZoneProperties['Shared Network Account Name'] = data.sharedNetworkAccountName;
    }
    if (hasValue(data?.sharedNetworkAccountId)) {
        landingZoneProperties['Shared Network Account ID'] = data.sharedNetworkAccountId;
    }

    // Only add Landing Zone Details section if there are properties to display
    if (Object.keys(landingZoneProperties).length > 0) {
        resources.push({
            'Resource Type': 'Landing Zone Details',
            'Resource Properties': landingZoneProperties,
        });
    }

    // 3. Tags (only for API responses)
    if (isApiResponse && data?.techInputJson?.resource_tags) {
        resources.push({
            'Resource Type': 'Tags',
            'Resource Properties': data.techInputJson.resource_tags,
        });
    }

    // 4. Access Management - only include if there are properties to display
    const accessManagementProperties = {};

    // Active Directory Group is always added if we have data
    if (data) {
        // If both activeDirectoryGroupOwnerId and contributorAdGroups are missing, show 'No'.
        let adGroupValue = 'Yes';
        let showInfo = false;
        if (!data?.activeDirectoryGroupOwnerId && !hasValue(data?.contributorAdGroups)) {
            adGroupValue = 'No';
        } else if (data?.activeDirectoryGroupOwnerId) {
            adGroupValue = 'No';
            showInfo = data?.activeDirectoryGroupOwnerId;
        }
        accessManagementProperties['Active Directory Group'] = {
            value: adGroupValue,
            showInfo,
            infoMsg: `You selected 'No' for using an existing Active Directory group. A new Active Directory group will therefore be created and linked to your AWS account as part of the account creation process.`,
        };
    }

    // Add privileged AD groups if they exist
    if (hasValue(data?.contributorAdGroups)) {
        accessManagementProperties['privileged Active Directory Group'] = Array.isArray(data.contributorAdGroups)
            ? data.contributorAdGroups.map((app) => app?.name || app).join(', ')
            : data.contributorAdGroups;
    }

    // Add AD group owner if exists
    if (hasValue(data?.activeDirectoryGroupOwnerId)) {
        accessManagementProperties['Active Directory Group Owner'] = data.activeDirectoryGroupOwnerId;
    }

    // Only add Access Management section if there are properties to display
    if (Object.keys(accessManagementProperties).length > 0) {
        resources.push({
            'Resource Type': 'Access Management',
            'Resource Properties': accessManagementProperties,
        });
    }

    return { ...generalInfo, resources };
};

export default formatAwsAccountVendingPreview;
